import React, { FC, useContext, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { PropertyType, useStore } from '../../../../common';
import { BoxDanger, EmptyState } from 'esa-ui-library';
import { cloneDeep, find, isEmpty, pick, toPairs } from 'lodash';
import { getRows } from '../RowProvider';
import styles from './GamblingReportTable.module.scss';
import { TableActions } from '../../../components/TableActions/TableActions';
import { tableActions } from '../../../components/TableActions/TableActions.const';
import {
	GamblingListType,
	calculateTotal,
	cellDataFormatter,
	exportFormatter,
	getFieldsValuesBySelectedType,
	getFiltersUrl,
	getGamblingFields,
	mergeSameRowsForGrandTotal,
	moveAllGamesCategoryToBottom,
	sortGamblingReportData,
} from '../GamblingReport.helper';
import { AuthContext } from '../../../../logic';
import { getColumns, getInitialColumns } from '../../../../common/services/getColumns';
import usePrevious from '../../../../common/hooks/usePrevious';
import { SkeletonRow } from './SkeletonRow';
import TableHeadCell from './TableHead/TableHeadCell';
import { filtersMap, mergeRowMap } from '../GamblingReport.const';
import { GRSort } from '../types/gambling-report-interfaces';
import { GRColumsFormatKey } from '../types/gambling-report-types';

interface GamblingReportTableProps {
	data: PropertyType;
	isLoading?: boolean;
}

const GamblingReportTable: FC<GamblingReportTableProps> = ({ data, isLoading }): JSX.Element => {
	const [state, dispatch] = useStore();
	const { user, token } = useContext(AuthContext);
	const { filters, columns, selectedTotal, averageRates } = state;
	const [sortObject, setSortObject] = useState<GRSort | null>(null);
	const prevStartDate = usePrevious(find(filters, ['name', 'start_date']));
	const prevEndDate = usePrevious(find(filters, ['name', 'end_date']));

	const listOneFilter: GamblingListType = find(filters, ['name', 'aggregators'])?.value;
	const gamblingFields = listOneFilter ? getGamblingFields(listOneFilter) : [];
	const isGrandTotal = selectedTotal.value === 'grand_total';

	const dataMapper = (sort: GRSort | null, dataCallback: any) => {
		let result: PropertyType[] = [];
		let plainData: PropertyType[] = [];
		const filteredData = !isGrandTotal ? pick(data, [selectedTotal.value]) : data;

		const rates = isGrandTotal ? averageRates : undefined;

		toPairs(filteredData).map((pair) => {
			const aggregator = filtersMap[listOneFilter];
			toPairs(pair[1]).map((data) => {
				const objectValues = data[1] as PropertyType;
				const addedValues = getFieldsValuesBySelectedType(
					aggregator,
					data[0],
					user?.partners,
					state[aggregator],
					objectValues.partner_id
				);

				const rowsData: PropertyType = {
					currency: pair[0],
					...objectValues,
					...addedValues,
				};
				plainData.push(rowsData);
			});
		});

		plainData = plainData.map((obj) => cellDataFormatter(obj, rates));
		if (isGrandTotal) {
			plainData = mergeSameRowsForGrandTotal(plainData, mergeRowMap[listOneFilter]);
		}
		result = sortGamblingReportData(sort, plainData);

		if (listOneFilter === 'category_ids') {
			result = moveAllGamesCategoryToBottom(result);
		}

		if (dataCallback) {
			return result.map((row) => dataCallback(row, rates));
		}

		return result;
	};

	const onSortData = (sortParams: GRSort) => {
		setSortObject(sortParams);
	};

	const renderExportData = () => {
		const result = dataMapper(sortObject, null);
		const sum = calculateTotal(result, isGrandTotal);
		const data = exportFormatter([...result, sum], isGrandTotal, averageRates);

		return { data, total: result.length };
	};

	const renderData = (sortParams: GRSort | null) => {
		const rowsMapper = (r: PropertyType, rates: PropertyType[]) => {
			const rows = getRows([cloneDeep(r)], columns, rates, false, isGrandTotal);
			return rows.map((dt) => (
				<tr className={`${styles.Row}`} key={uuid()} data-testid='report-data-row'>
					{dt.map((c: PropertyType) => (
						<td key={uuid()} className={`${styles.Cell}`}>
							{c}
						</td>
					))}
				</tr>
			));
		};
		const tableData = dataMapper(sortParams, rowsMapper);
		return tableData;
	};

	const renderTotals = () => {
		const list = dataMapper(sortObject, null);
		const sum = calculateTotal(list, isGrandTotal);
		const rows = getRows([sum], columns, undefined, true, isGrandTotal);

		return rows.map((dt) => (
			<tr className={`${styles.TableFooter} ${styles.Row}`} key={uuid()} data-testid='subtotal-row'>
				{dt.map((c: PropertyType) => (
					<td key={uuid()} className={`${styles.Cell}`}>
						{c}
					</td>
				))}
			</tr>
		));
	};

	const renderHeaderCells = (cells: GRColumsFormatKey[]) => (
		<tr>
			{cells.map((c) => (
				<TableHeadCell data={c} key={c} onSort={onSortData} />
			))}
		</tr>
	);

	const renderSkeleton = (cells: string[]) => {
		const rows: React.ReactElement[] = [];
		for (let index = 0; index < 10; index++) {
			const row = <SkeletonRow key={uuid()} cells={cells} />;
			rows.push(row);
		}
		return rows;
	};

	useEffect(() => {
		if (listOneFilter) {
			setSortObject(null);
			dispatch('INIT_COLUMNS', {
				columns: getColumns('gamblingReport', gamblingFields),
			});
			dispatch('UPDATE_INITIAL_COLUMNS', getInitialColumns(gamblingFields));
		}
	}, [listOneFilter]);

	useEffect(() => {
		const start = find(filters, ['name', 'start_date']);
		const end = find(filters, ['name', 'end_date']);
		if (start && end && (prevStartDate?.value !== start?.value || prevEndDate?.value !== end?.value)) {
			dispatch('RATES_INIT', { token, start_date: start.value, end_date: end.value });
		}
	}, [filters]);

	const header = renderHeaderCells(columns);
	const body = renderData(sortObject);
	const totals = listOneFilter === 'category_ids' ? null : renderTotals();

	return (
		<>
			{!isLoading && !isEmpty(body) && (
				<TableActions
					exportData={{
						data: renderExportData(),
						filtersQuery: getFiltersUrl(filters),
						csvHeaders: gamblingFields,
					}}
					filters={[tableActions.EXPORT, tableActions.CURRENCY, tableActions.COLUMNS]}
				/>
			)}
			<section data-testid='table-component' className={`${styles.TableWrapper}`}>
				{!isLoading && isEmpty(body) ? (
					<EmptyState
						title='No results found for selected search parameters'
						icon={<BoxDanger />}
						dataId='empty-state-component'
					/>
				) : (
					<table className={styles.Table}>
						<thead className={styles.TableHead}>{header}</thead>
						<tbody className={styles.TableBody}>
							{!isLoading && totals}
							{isLoading ? renderSkeleton(columns) : body}
							{!isLoading && totals}
						</tbody>
						<thead className={styles.TableHead}>{header}</thead>
					</table>
				)}
			</section>
		</>
	);
};

export { GamblingReportTable };
